// DISABLED: Excel export functionality disabled due to organization policy
// import ExcelJS from 'exceljs';
import type { VerticalLine } from '../types';

export interface ExportData {
  sectionName: string;
  sensors: string[];
  verticalLines: VerticalLine[];
  enabledSensors: Record<string, boolean>;
  sensorColors: Record<string, string>;
  showPercentage?: boolean;
  benchmarkLineId?: string | null;
  chartData?: any;
  xAxisType?: 'date' | 'numeric';
  xAxisLabel?: string;
  selectedVerticalLineIds?: string[]; // For highlighting selected markers
  // Additional chart annotations
  harmonics?: Array<{ x: number; label: string }>;
  sideBands?: Array<{ x: number; label: string }>;
  // Chart image for embedding
  chartImageBase64?: string;
}

/**
 * DISABLED: Excel export functionality disabled due to organization policy
 * Exports data to Excel using ExcelJS (MIT licensed, 100% free)
 * Creates formatted tables with EMBEDDED CHARTS and annotations
 */
export async function exportComparisonTableToExcel(data: ExportData): Promise<void> {
  console.warn('[Excel Export] Excel export is disabled due to organization policy');
  alert('Excel export is currently disabled due to organization policy. Please contact your administrator.');
  return;

  /* DISABLED CODE:
  const {
    sectionName,
    sensors,
    verticalLines,
    enabledSensors,
    sensorColors,
    showPercentage = false,
    benchmarkLineId,
    chartData,
    xAxisType = 'date',
    xAxisLabel = 'Time',
    selectedVerticalLineIds = [],
    harmonics = [],
    sideBands = [],
  } = data;

  // Filter enabled sensors
  const enabledSensorsList = sensors.filter((s) => enabledSensors[s]);

  // Determine export mode
  const hasMarkers = verticalLines && verticalLines.length > 0;
  const hasChartData = chartData && chartData.data && chartData.data.length > 0;

  if (!hasMarkers && !hasChartData) {
    return;
  }

  try {
    // Create workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'DataMind Dashboard';
    workbook.created = new Date();

    // Export chart data if available
    if (hasChartData) {
      await exportFullChartDataWithChart(
        workbook,
        data,
        enabledSensorsList,
        xAxisLabel,
        verticalLines,
        benchmarkLineId,
        selectedVerticalLineIds,
        harmonics,
        sideBands
      );
    }

    // Export comparison table if markers exist (separate sheet with transposed format)
    if (hasMarkers) {
      exportComparisonTable(workbook, data, enabledSensorsList, selectedVerticalLineIds);
    }

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${sectionName.replace(/\s+/g, '-')}-${timestamp}.xlsx`;

    // Write file and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('[Excel Export] Error during export:', error);
    throw error;
  }
  */
}

/**
 * DISABLED: Excel export functionality disabled due to organization policy
 * Export full chart time-series data with table
 * Includes vertical lines, harmonics, and sidebands as annotations
 */
async function exportFullChartDataWithChart(
  workbook: any, // ExcelJS.Workbook,
  data: ExportData,
  enabledSensorsList: string[],
  xAxisLabel: string,
  verticalLines: VerticalLine[],
  benchmarkLineId: string | null | undefined,
  selectedVerticalLineIds: string[],
  harmonics: Array<{ x: number; label: string }>,
  sideBands: Array<{ x: number; label: string }>
): Promise<void> {
  // DISABLED: Function body disabled
  return;
  /* DISABLED CODE:
  const { chartData, xAxisType, sectionName } = data;

  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return;
  }

  // Create data table worksheet
  const dataSheet = workbook.addWorksheet('Data Table');

  // Get x-axis data
  const firstTrace = chartData.data[0];
  const xValues = firstTrace.x || [];

  // Map traces to sensors - try by name first, then by index
  const sensorDataMap: Record<string, number[]> = {};

  // First try to match by trace name
  chartData.data.forEach((trace: any) => {
    if (trace.name && enabledSensorsList.includes(trace.name)) {
      sensorDataMap[trace.name] = trace.y || [];
    }
  });

  // If name matching didn't get all sensors, use index matching for remaining sensors
  if (Object.keys(sensorDataMap).length < enabledSensorsList.length) {
    // Use index-based mapping for all sensors
    enabledSensorsList.forEach((sensorName, index) => {
      if (!sensorDataMap[sensorName] && index < chartData.data.length) {
        sensorDataMap[sensorName] = chartData.data[index].y || [];
      }
    });
  }

  // Build headers
  const headers = [xAxisLabel, ...enabledSensorsList];

  // Add header row
  dataSheet.addRow(headers);

  // Style header row
  const headerRow = dataSheet.getRow(1);
  headerRow.font = { bold: true, size: 11, name: 'Calibri', color: { argb: 'FFFFFFFF' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF4472C4' },
  };
  headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
  headerRow.height = 25;

  // Helper function to find closest x-value index
  const findClosestXIndex = (targetValue: any): number => {
    if (xAxisType === 'date') {
      const targetTime = new Date(targetValue).getTime();
      let closestIndex = 0;
      let minDiff = Math.abs(new Date(xValues[0]).getTime() - targetTime);

      for (let i = 1; i < xValues.length; i++) {
        const diff = Math.abs(new Date(xValues[i]).getTime() - targetTime);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = i;
        }
      }
      return closestIndex;
    } else {
      // Numeric comparison
      let closestIndex = 0;
      let minDiff = Math.abs(xValues[0] - targetValue);

      for (let i = 1; i < xValues.length; i++) {
        const diff = Math.abs(xValues[i] - targetValue);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = i;
        }
      }
      return closestIndex;
    }
  };

  // Build map of row indices to highlight
  const rowHighlights: Record<number, 'benchmark' | 'selected'> = {};
  verticalLines.forEach((vl) => {
    const targetValue = vl.timestamp;
    if (targetValue !== undefined) {
      const dataIndex = findClosestXIndex(targetValue);
      const excelRowNumber = 1 + dataIndex + 1; // Row 1 is header, data starts at row 2

      if (vl.id === benchmarkLineId) {
        rowHighlights[excelRowNumber] = 'benchmark';
      } else if (selectedVerticalLineIds.includes(vl.id)) {
        rowHighlights[excelRowNumber] = 'selected';
      }
    }
  });

  // Data rows (limit to 10,000 rows for Excel performance)
  const maxRows = Math.min(xValues.length, 10000);
  for (let i = 0; i < maxRows; i++) {
    const row: any[] = [];

    // X-axis value
    const xValue = xValues[i];
    if (xAxisType === 'date') {
      const dateStr = xValue instanceof Date ? xValue.toISOString() : new Date(xValue).toISOString();
      row.push(dateStr);
    } else {
      const numVal = typeof xValue === 'number' ? Number(xValue.toFixed(4)) : xValue;
      row.push(numVal);
    }

    // Y values for each sensor
    enabledSensorsList.forEach((sensorName) => {
      const yValues = sensorDataMap[sensorName];
      if (yValues && yValues[i] !== undefined && yValues[i] !== null) {
        row.push(Number(yValues[i].toFixed(4)));
      } else {
        row.push(null);
      }
    });

    const newRow = dataSheet.addRow(row);

    // Add borders to all cells
    newRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD0D0D0' } },
        left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
        bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
        right: { style: 'thin', color: { argb: 'FFD0D0D0' } },
      };
      cell.alignment = { horizontal: 'right', vertical: 'middle' };
    });
  }

  // Apply row highlighting for vertical lines
  Object.entries(rowHighlights).forEach(([rowNumStr, highlightType]) => {
    const rowNum = parseInt(rowNumStr);
    const row = dataSheet.getRow(rowNum);

    const fillColor = highlightType === 'benchmark'
      ? { argb: 'FFFFD700' } // Gold for benchmark
      : { argb: 'FFADD8E6' }; // Light blue for selected

    row.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: fillColor,
      };
    });
    row.commit();
  });

  // Set column widths
  dataSheet.getColumn(1).width = 20;
  for (let i = 2; i <= enabledSensorsList.length + 1; i++) {
    dataSheet.getColumn(i).width = 15;
  }

  // Add table starting from the header row
  dataSheet.addTable({
    name: 'ChartDataTable',
    ref: 'A1',
    headerRow: true,
    totalsRow: false,
    columns: headers.map((h) => ({ name: h, filterButton: true })),
    rows: [],
  });

  // Create separate sheet for chart image if provided
  if (data.chartImageBase64) {
    try {
      const chartSheet = workbook.addWorksheet('Chart Visualization');

      // Add the image to the workbook
      const imageId = workbook.addImage({
        base64: data.chartImageBase64,
        extension: 'png',
      });

      // Position the image at the top-left
      chartSheet.addImage(imageId, {
        tl: { col: 0, row: 0 },
        ext: { width: 1200, height: 700 },
      });
    } catch (error) {
      // Chart image is optional
    }
  }
  */
}

/**
 * DISABLED: Excel export functionality disabled due to organization policy
 * Export comparison table (markers) in TRANSPOSED format
 * Markers as column headers, sensors as row headers
 */
function exportComparisonTable(
  workbook: any, // ExcelJS.Workbook,
  data: ExportData,
  enabledSensorsList: string[],
  selectedVerticalLineIds: string[]
): void {
  // DISABLED: Function body disabled
  return;
  /* DISABLED CODE:
  const { verticalLines, showPercentage, benchmarkLineId } = data;

  // Find benchmark line
  const benchmarkLine = verticalLines.find((line) => line.id === benchmarkLineId) || verticalLines[0];

  // Create worksheet
  const worksheet = workbook.addWorksheet('Comparison Table');

  // Headers: First column is "Sensor", then 3 columns for each marker (Value, Δ Abs, Δ %)
  const headers = ['Sensor'];
  verticalLines.forEach((line) => {
    const label = line.label || new Date(line.timestamp).toLocaleString();
    let markerLabel = label;

    // Mark benchmark and selected lines in header
    if (line.id === benchmarkLineId) {
      markerLabel = `⭐ ${label}`;
    } else if (selectedVerticalLineIds.includes(line.id)) {
      markerLabel = `📌 ${label}`;
    }

    // Add 3 columns for each marker
    headers.push(`${markerLabel} (Value)`);
    headers.push(`${markerLabel} (Δ Abs)`);
    headers.push(`${markerLabel} (Δ %)`);
  });
  worksheet.addRow(headers);

  // Style header row
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true, size: 11 };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF4472C4' },
  };
  headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
  headerRow.height = 25;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
  });

  // Data rows: Each sensor is a row
  enabledSensorsList.forEach((sensorName) => {
    const row: any[] = [sensorName];

    verticalLines.forEach((line) => {
      const value = line.values?.[sensorName];
      const benchmarkValue = benchmarkLine?.values?.[sensorName];

      if (value !== undefined && value !== null) {
        // Column 1: Original value
        row.push(Number(value.toFixed(2)));

        // Column 2: Absolute difference from benchmark
        if (benchmarkValue !== undefined && benchmarkValue !== null) {
          const absDiff = value - benchmarkValue;
          row.push(Number(absDiff.toFixed(2)));
        } else {
          row.push('-');
        }

        // Column 3: Percentage difference from benchmark
        if (benchmarkValue !== undefined && benchmarkValue !== null && benchmarkValue !== 0) {
          const percentDiff = ((value - benchmarkValue) / benchmarkValue) * 100;
          row.push(Number(percentDiff.toFixed(2)));
        } else {
          row.push('-');
        }
      } else {
        row.push('-', '-', '-');
      }
    });

    worksheet.addRow(row);
  });

  // Set column widths (3 columns per marker)
  worksheet.getColumn(1).width = 25; // Sensor name column
  const totalColumns = 1 + (verticalLines.length * 3);
  for (let i = 2; i <= totalColumns; i++) {
    worksheet.getColumn(i).width = 15;
  }

  // Highlight benchmark and selected marker groups (3 columns each)
  verticalLines.forEach((line, index) => {
    const startColIndex = 2 + (index * 3); // Each marker has 3 columns

    if (line.id === benchmarkLineId) {
      // Gold background for benchmark (all 3 columns)
      for (let col = startColIndex; col < startColIndex + 3; col++) {
        const column = worksheet.getColumn(col);
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip header
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFD700' },
            };
          }
        });
      }
    } else if (selectedVerticalLineIds.includes(line.id)) {
      // Light blue background for selected (all 3 columns)
      for (let col = startColIndex; col < startColIndex + 3; col++) {
        const column = worksheet.getColumn(col);
        column.eachCell((cell, rowNumber) => {
          if (rowNumber > 1) { // Skip header
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFADD8E6' },
            };
          }
        });
      }
    }
  });
  */
}
